import {Modal} from "@ui";
import dayjs from "dayjs";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button, SecondaryButton} from "@/components/Button";
import useIsMobile from "@/hooks/useIsMobile";
import {dropStorage, getStorage, setStorage} from "@/store/util.store";
import defaultBanner from "../../../../../assets/images/banner movil_deposit_balance.avif";
import useGetUserInfo from "../../../../../hooks/useGetUserInfo";
import useGetUserLastConnection from "../../../../../hooks/useGetUserLastConnection.store";
import usePagoMovilRegistration from "../../../../../hooks/usePagoMovilRegistration";
import useGetUserWallet from "../../../../../hooks/wallet/useGetUserWallet";
import {trackDepositButtonMenuClick} from "../../../../../providers/analytics";
import {useAppConfig} from "../../../../../store/useAppConfig.store";
import {useAuthStore} from "../../../../../store/useAuth.store";
import {useBalanceStore} from "../../../../../store/useBalance.store";
import styles from "./ModalSessionInfo.module.css";

const formatDate = (dateString, atTimeLabel) => {
  return dayjs(dateString).format(`DD/MM/YYYY [${atTimeLabel}] HH:mm`);
};

const CURRENCY_ACCOUNTS = [
  {currency: "VES", label: "Bs"},
  {currency: "USD", label: "USD"},
  {currency: "USDT", label: "USDT"},
  {currency: "CLP", label: "CLP"},
];

const ModalSessionInfo = () => {
  const [visible, setVisible] = useState(false);
  const authStatus = useAuthStore(state => state.authStatus);
  const {data} = useGetUserInfo();
  const {t} = useTranslation();
  const isMobile = useIsMobile({breakpoint: 600});

  const handleBankAccountModal = useBalanceStore.getState().handleBankAccountModal;
  const {triggerRegistrationModalIfNeeded} = usePagoMovilRegistration();

  const handleDepositAction = async () => {
    trackDepositButtonMenuClick();
    const modalTriggered = await triggerRegistrationModalIfNeeded("deposit", true);
    if (!modalTriggered) {
      handleBankAccountModal();
    }
  };

  useEffect(() => {
    if (authStatus === "authenticated") {
      const hasShown = getStorage("modalSessionInfoShown");
      if (!hasShown) {
        setVisible(true);
        setStorage("modalSessionInfoShown", "true");
      }
    } else {
      setVisible(false);
      dropStorage("modalSessionInfoShown");
    }
  }, [authStatus]);

  const handleClose = () => setVisible(false);

  const {getAccountByCurrency} = useGetUserWallet();

  const activeAccounts = CURRENCY_ACCOUNTS.map(({currency, label}) => {
    const account = getAccountByCurrency(currency);
    return {currency, label, balance: account?.formattedBalance || "0.00", account};
  }).filter(item => item.account?.balance > 0);

  const userNameRaw = data?.name || "";
  const userName = userNameRaw.charAt(0).toUpperCase() + userNameRaw.slice(1);

  const {data: lastConnectionData} = useGetUserLastConnection();
  const atTimeLabel = t("homeModalSessionInfoAtTime", {
    defaultValue: t("homeModalSessionInfoLastConnectionAtTime"),
  });
  const lastConnection = lastConnectionData?.data?.previous_connection
    ? formatDate(lastConnectionData.data.previous_connection, atTimeLabel)
    : "-";

  const authModalImages = useAppConfig(state => state.authModalImages);
  const bannerImage = authModalImages?.loginMobileImage || defaultBanner;

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
      className="welcome-modal-home-info"
      styles={{
        mask: {backdropFilter: "blur(6px)", backgroundColor: "var(--overlay-bg)"},
        content: {fontFamily: "var(--font-family)", padding: 0, overflow: "hidden", borderRadius: 16},
        body: {padding: 0},
      }}
      width={isMobile ? "100%" : 520}
      closable={false}
    >
      <div className={styles.wrapper}>
        <img
          src={bannerImage}
          alt=""
          className={styles.banner}
        />

        <div className={styles.body}>
          <div className={styles.header}>
            <div className={styles.greetingBlock}>
              <p className={styles.greeting}>{t("homeModalSessionInfotitle")}</p>
              {userName && <p className={styles.userName}>{userName}</p>}
            </div>
            <div className={styles.lastAccess}>
              <p className={styles.lastAccessLabel}>{t("homeModalSessionInfoLastConnection")}</p>
              <p className={styles.lastAccessValue}>{lastConnection}</p>
            </div>
          </div>

          {activeAccounts.length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.balancesRow}>
                {activeAccounts.map(item => (
                  <div
                    key={item.currency}
                    className={styles.balanceCard}
                  >
                    <p className={styles.balanceLabel}>{item.label}</p>
                    <p className={styles.balanceValue}>{item.balance}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className={styles.actions}>
            <Button
              variant="tertiary-hover"
              className={styles.depositBtn}
              onClick={() => {
                handleDepositAction();
                handleClose();
              }}
              aria-label={t("homeModalSessionInfoGoToDeposit")}
            >
              {t("homeModalSessionInfoGoToDeposit")}
            </Button>
            <SecondaryButton
              onClick={handleClose}
              aria-label={t("homeModalSessionInfoContinue")}
              className={styles.continueBtn}
            >
              {t("homeModalSessionInfoContinue")}
            </SecondaryButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalSessionInfo;
